import React, { useState, useEffect } from "react";
import Layout from "../components/Layout";
import editIcon from "../assets/editIcon.svg";
import { FaCheck } from "react-icons/fa";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import api from "../api";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Spinner from "../components/Spinner";

export default function ProfilePage() {
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState({});
  const [formValues, setFormValues] = useState({
    name: "",
    email: "",
    govId: "",
    phone: "",
    newPassword: "",
  });

  const [isEditable, setIsEditable] = useState({
    name: false,
    email: false,
    id: false,
    phone: false,
  });

  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false); // Estado para mostrar u ocultar la contraseña

  // Manejar cambios en los inputs
  const handleChange = (field, value) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Alternar edición
  const toggleEdit = (field) => {
    setIsEditable((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  // Alternar visibilidad de la contraseña
  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  // Guardar cambios en información personal
  const handleSavePersonalInfo = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError(null);
    setFieldErrors({});

    // Descomponer el nombre completo en firstName y lastName
    const [firstName, ...lastNameParts] = formValues.name.split(" ");
    const lastName = lastNameParts.join(" "); // Une las partes restantes en caso de que haya más de un apellido

    const newValues = {
      firstName: firstName,
      lastName: lastName,
      govId: formValues.govId,
      email: formValues.email,
      phone: formValues.phone,
      role: user.role,
    };

    try {
      await api.users.update(user.id, newValues);
      const updatedUser = await api.users.getMe();
      setUser(updatedUser);
      setIsEditable({
        name: false,
        email: false,
        id: false,
        phone: false,
      });
      toast.success("Datos guardados con éxito");
    } catch (error) {
      if (error.message) {
        const fieldErrors = error.message;
        setFieldErrors(fieldErrors);
      } else {
        setError("Error al modificar el usuario. Intenta nuevamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Guardar contraseña
  const handleSavePassword = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError(null);
    setFieldErrors({});

    try {
      await api.users.changePassword(formValues.newPassword);
      const updatedUser = await api.users.getMe();
      toast.success("Se cambió la contraseña exitosamente");
      setUser(updatedUser);
    } catch (error) {
      if (error.message) {
        const fieldErrors = error.message;
        setFieldErrors(fieldErrors);
      } else {
        setError("Error al modificar la contraseña. Intenta nuevamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    async function fetchMe() {
      try {
        setLoading(true);
        const response = await api.users.getMe();
        setUser(response);
        setLoading(false);
      } catch (error) {
        setLoading(false);
        console.error("Error fetching user data:", error);
      }
    }
    fetchMe();
  }, []);

  useEffect(() => {
    if (user) {
      setFormValues({
        name: `${user.firstName || ""} ${user.lastName || ""}`,
        email: user.email || "",
        govId: user.govId || "",
        phone: user.phone || "",
        newPassword: "",
      });
    }
  }, [user]);

  const [opacityLevel, setOpacityLevel] = useState(0);

  useEffect(() => {
    if (loading) {
      let intervalId = setInterval(() => {
        setOpacityLevel((prev) => Math.min(prev + 0.1, 1)); // Incrementa la opacidad hasta 1
      }, 500); // Ajusta el intervalo para controlar la velocidad
      return () => clearInterval(intervalId); // Limpia el intervalo al desmontar
    } else {
      setOpacityLevel(1); // Asegúrate de que la opacidad esté al máximo al terminar de cargar
    }
  }, [loading]);

  return (
    <Layout>
      <div
        className="p-8 space-y-20 bg-[#F8F9FB] transition-opacity ease-in-out"
        style={{
          opacity: opacityLevel,
          transition: "opacity 0.5s linear",
        }}
      >
        {loading ? (
          <Spinner />
        ) : (
          <>
            <h1 className="poppins-bold text-lg text-[#1F384C] tracking-wider">
              Editar perfil
            </h1>
            <ToastContainer />
            <div className="flex flex-col md:flex-row">
              <div className="flex-1 pr-20 space-y-6">
                <h2 className="poppins-bold text-sm text-[#1F384C]">
                  Información personal
                </h2>
                <div className="grid grid-cols-2 gap-y-[30px] gap-x-[40px]">
                  {[
                    { label: "Nombre y apellido", field: "name" },
                    { label: "Correo electrónico", field: "email" },
                    { label: "Cédula de identidad", field: "govId" },
                    { label: "Celular", field: "phone" },
                  ].map(({ label, field }) => (
                    <div key={field} className="relative">
                      <label className="text-sm text-[#374151] font-medium block mb-1">
                        {label}
                      </label>
                      <div className="flex items-center border rounded-lg px-2 bg-[#FEFFFF]">
                        <input
                          type="text"
                          value={formValues[field]}
                          onChange={(e) => handleChange(field, e.target.value)}
                          className="flex-1 p-2 text-xs outline-none bg-[#FEFFFF]"
                          style={{
                            color: isEditable[field] ? "black" : "#8F99A6",
                          }}
                          disabled={!isEditable[field]}
                        />
                        <button
                          onClick={() => toggleEdit(field)}
                          className="text-sm text-blue-500"
                        >
                          {isEditable[field] ? (
                            <FaCheck color="#0080FF" />
                          ) : (
                            <img src={editIcon} alt="editar" />
                          )}
                        </button>
                      </div>
                      {fieldErrors[field] && (
                        <p className="text-red-600 text-xs mt-1">
                          {fieldErrors[field]}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
                <button
                  onClick={handleSavePersonalInfo}
                  className="poppins-medium bg-[#BE202F] w-full mt-auto text-white text-xs py-4 px-4 rounded-2xl tracking-wider"
                >
                  Guardar cambios
                </button>
              </div>
              <div className="w-[1px] bg-[#D9D9D9] mx-4"></div>
              <div className="w-[40%] pl-20 space-y-6">
                <h2 className="poppins-bold text-sm text-[#1F384C]">
                  Administración de contraseña
                </h2>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm text-[#374151] font-medium block mb-1">
                      Nueva contraseña
                    </label>
                    <div className="flex items-center border rounded-lg px-2 bg-[#FEFFFF]">
                      <input
                        type={showPassword ? "text" : "password"}
                        value={formValues.newPassword}
                        onChange={(e) =>
                          handleChange("newPassword", e.target.value)
                        }
                        className="flex-1 p-2 text-sm outline-none"
                      />
                      <button
                        onClick={togglePasswordVisibility}
                        className="ml-2"
                      >
                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                    {fieldErrors.newPassword && (
                      <p className="text-red-600 text-xs mt-1">
                        {fieldErrors.newPassword}
                      </p>
                    )}
                  </div>
                </div>
                <button
                  onClick={handleSavePassword}
                  className="poppins-medium bg-[#BE202F] w-full mt-auto text-white text-xs py-4 px-4 rounded-2xl tracking-wider"
                >
                  Guardar contraseña
                </button>
              </div>
            </div>
            {error && <p className="text-red-600 mb-4">{error}</p>}
          </>
        )}
      </div>
    </Layout>
  );
}
